import {ClipboardIcon, PencilIcon, PlusIcon, TrashIcon} from '@heroicons/react/24/outline';
import {CheckIcon} from '@heroicons/react/24/solid';
import React from 'react';

import LoadingSpinner from 'src/components/LoadingSpinner';
import PageContainer from 'src/components/PageContainer';
import PageTitle from 'src/components/PageTitle';
import {useTranslations} from 'src/components/TranslationsContext';
import {useCurrentOrganization} from 'src/hooks/useAuth';
import useAuth from 'src/hooks/useAuth';
import useOrganizationApiKeys, {
  useCreateApiKey,
  useRevokeApiKey,
  useUpdateApiKeyLabel,
} from 'src/hooks/useOrganizationApiKeys';
import {ApiKeyScope} from 'src/lib/api/auth';
import * as tw from 'src/lib/tailwindClasses';

// Keep in sync with MAX_API_KEY_LABEL_LENGTH in backend/common/data_model/organization.py
const MAX_API_KEY_LABEL_LENGTH = 100;

export default function ApiSettings() {
  const {t, locale} = useTranslations();
  const auth = useAuth();
  const currentOrg = useCurrentOrganization()!;
  const apiKeysQuery = useOrganizationApiKeys(currentOrg.id);
  const createApiKeyMutation = useCreateApiKey(currentOrg.id);
  const revokeApiKeyMutation = useRevokeApiKey(currentOrg.id);
  const updateApiKeyLabelMutation = useUpdateApiKeyLabel(currentOrg.id);
  const apiKeys = apiKeysQuery.data;

  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
  const [newKeyScope, setNewKeyScope] = React.useState<ApiKeyScope>(ApiKeyScope.READONLY);
  const [newKeyExpirationDays, setNewKeyExpirationDays] = React.useState<number | null>(null);
  const [newKeyLabel, setNewKeyLabel] = React.useState('');
  const [editingKey, setEditingKey] = React.useState<string | null>(null);
  const [editingLabel, setEditingLabel] = React.useState('');

  // Check if user is owner (only owners and admins can manage API keys)
  const isOwnerOrAdminUser =
    auth.status === 'logged in' &&
    auth.userProfile &&
    (auth.userProfile.role === 'owner' || auth.userProfile.role === 'admin');

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(text);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCreateKey = () => {
    let expiresAt: string | null = null;
    if (newKeyExpirationDays !== null) {
      const date = new Date();
      date.setDate(date.getDate() + newKeyExpirationDays);
      expiresAt = date.toISOString();
    }
    createApiKeyMutation.mutate(
      {scope: newKeyScope, expiresAt, label: newKeyLabel.trim() || null},
      {onSuccess: () => setNewKeyLabel('')}
    );
  };

  const handleSaveLabel = (apiKey: string) => {
    updateApiKeyLabelMutation.mutate(
      {apiKey, label: editingLabel.trim() || null},
      {onSuccess: () => setEditingKey(null)}
    );
  };

  const handleRevokeKey = (apiKey: string) => {
    if (window.confirm(t('areYouSureYouWantToRevokeThisApiKey'))) {
      revokeApiKeyMutation.mutate(apiKey);
    }
  };

  const canSelfManage = (scope: ApiKeyScope) => {
    return scope === ApiKeyScope.READONLY || scope === ApiKeyScope.READWRITE;
  };

  const getScopeColors = (scope: ApiKeyScope) => {
    switch (scope) {
      case ApiKeyScope.READONLY:
        return 'bg-blue-100 text-blue-800';
      case ApiKeyScope.READWRITE:
        return 'bg-green-100 text-green-800';
      case ApiKeyScope.STREAM_IMAGERY:
        return 'bg-purple-100 text-purple-800';
      case ApiKeyScope.EMAIL_OBSERVATIONS:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <PageContainer>
      <PageTitle
        pageStack={[
          {title: currentOrg.name, to: '/:currentOrgIdPrefix/projects'},
          {title: t('settings'), to: '/:currentOrgIdPrefix/settings'},
          {title: t('api'), to: '/:currentOrgIdPrefix/settings/api'},
        ]}
      />
      <div>
        <div className="md:grid md:grid-cols-5 md:gap-6">
          <div className="md:col-span-2">
            <div className="px-4 sm:px-0">
              <h3 className="text-lg font-medium leading-6 text-gray-900">{t('apiKeys')}</h3>
              <div className="mt-1 text-sm text-gray-600">
                {t('apiUsageInfo')}{' '}
                <a
                  className="text-blue-600"
                  href="https://hydroforecast.com/support/category/187-hydroforecast-api/"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('documentation')}
                </a>
                . {!isOwnerOrAdminUser && `${t('createNewKey')} `}
                {!isOwnerOrAdminUser && (
                  <a href="mailto:team@hydroforecast.com">team@hydroforecast.com</a>
                )}
                {!isOwnerOrAdminUser && '.'}
                {isOwnerOrAdminUser && `${t('apiKeyEditWarning')} `}
              </div>
            </div>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-3">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                {/* Create new API key section for owners */}
                {isOwnerOrAdminUser && (
                  <div className="border-b border-gray-200 pb-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">
                      {t('createNewApiKey')}
                    </h4>
                    <div className="space-y-3">
                      <div className="flex flex-col space-y-1">
                        <label className="text-xs text-gray-500">{t('label')}</label>
                        <input
                          type="text"
                          value={newKeyLabel}
                          maxLength={MAX_API_KEY_LABEL_LENGTH}
                          placeholder={t('apiKeyLabelPlaceholder')}
                          onChange={(e) => setNewKeyLabel(e.target.value)}
                          className={`${tw.select} w-full`}
                        />
                      </div>
                      <div className="flex items-end space-x-3">
                        <div className="flex flex-col space-y-1">
                          <label className="text-xs text-gray-500">{t('scope')}</label>
                          <select
                            value={newKeyScope}
                            onChange={(e) => setNewKeyScope(e.target.value as ApiKeyScope)}
                            className={`${tw.select} w-32`}
                          >
                            <option value={ApiKeyScope.READONLY}>{ApiKeyScope.READONLY}</option>
                            <option value={ApiKeyScope.READWRITE}>{ApiKeyScope.READWRITE}</option>
                          </select>
                        </div>
                        <div className="flex flex-col space-y-1">
                          <label className="text-xs text-gray-500">{t('expiration')}</label>
                          <select
                            value={newKeyExpirationDays ?? ''}
                            onChange={(e) =>
                              setNewKeyExpirationDays(
                                e.target.value === '' ? null : Number(e.target.value)
                              )
                            }
                            className={`${tw.select} w-32`}
                          >
                            <option value="">{t('never')}</option>
                            <option value="90">{t('nDays', {n: '90'})}</option>
                            <option value="120">{t('nDays', {n: '120'})}</option>
                            <option value="365">{t('nDays', {n: '365'})}</option>
                          </select>
                        </div>
                        <button
                          onClick={handleCreateKey}
                          disabled={createApiKeyMutation.isLoading}
                          className={`${tw.buttonPrimary} inline-flex items-center h-8`}
                        >
                          {/*<PlusIcon className="h-4 w-4 mr-2" />*/}
                          {createApiKeyMutation.isLoading ? t('creating') : t('createKey')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Existing API keys */}
                {apiKeys ? (
                  apiKeys
                    .filter((key) => !key.expired)
                    .map((key, i) => {
                      const isCopied = copiedKey === key.key;
                      // Relabeling doesn't change API access, so it isn't limited to the
                      // self-service scopes the way revoking is.
                      const canEdit = isOwnerOrAdminUser;
                      const canRevoke = isOwnerOrAdminUser && canSelfManage(key.scope);

                      return (
                        <div className="border border-gray-200 rounded-lg p-4" key={i}>
                          <div className="mb-2">
                            {editingKey === key.key ? (
                              <div className="flex items-center space-x-2">
                                <input
                                  type="text"
                                  autoFocus
                                  value={editingLabel}
                                  maxLength={MAX_API_KEY_LABEL_LENGTH}
                                  placeholder={t('apiKeyLabelPlaceholder')}
                                  onChange={(e) => setEditingLabel(e.target.value)}
                                  className={`${tw.select} flex-1 min-w-0`}
                                />
                                <button
                                  onClick={() => handleSaveLabel(key.key)}
                                  disabled={updateApiKeyLabelMutation.isLoading}
                                  className={`${tw.button} text-xs text-blue-700 bg-blue-50 border !border-blue-300 hover:bg-blue-100 focus:ring-blue-500 disabled:opacity-50`}
                                >
                                  {t('save')}
                                </button>
                                <button
                                  onClick={() => setEditingKey(null)}
                                  className={`${tw.button} text-xs`}
                                >
                                  {t('cancelChanges')}
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <span
                                  className={`text-sm break-words ${
                                    key.label ? 'font-medium text-gray-900' : 'italic text-gray-500'
                                  }`}
                                >
                                  {key.label ??
                                    t('apiKeyCreatedOnDate', {
                                      date: new Date(key.createdAt).toLocaleDateString(locale),
                                    })}
                                </span>
                                {canEdit && (
                                  <button
                                    title={t('edit')}
                                    onClick={() => {
                                      setEditingKey(key.key);
                                      setEditingLabel(key.label ?? '');
                                    }}
                                    className={`${tw.button} text-xs`}
                                  >
                                    <PencilIcon className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 mb-2">
                            <code className="flex-1 text-sm font-mono text-gray-700 font-semibold">
                              {key.key}
                            </code>
                            <button
                              onClick={() => copyToClipboard(key.key)}
                              className={`${tw.button} text-xs text-blue-700 bg-blue-50 border !border-blue-300 hover:bg-blue-100 focus:ring-blue-500`}
                            >
                              {isCopied ? (
                                <CheckIcon className="h-3 w-3 mr-1 text-green-500" />
                              ) : (
                                <ClipboardIcon className="h-3 w-3 mr-1" />
                              )}
                              {isCopied ? t('copied') : t('copy')}
                            </button>
                            {canRevoke && (
                              <button
                                onClick={() => handleRevokeKey(key.key)}
                                disabled={revokeApiKeyMutation.isLoading}
                                className={`${tw.button} text-xs text-red-700 bg-red-50 border !border-red-300 hover:bg-red-100 focus:ring-red-500 disabled:opacity-50`}
                              >
                                <TrashIcon className="h-3 w-3 mr-1" />
                                {t('revoke')}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center space-x-2">
                              <span className="text-gray-500">{t('scope')}:</span>
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getScopeColors(key.scope)}`}
                              >
                                {key.scope}
                              </span>
                            </div>
                            <span className="text-gray-500">
                              {key.expiresAt
                                ? t('createdDateExpiresOn', {
                                    date: new Date(key.createdAt).toLocaleDateString(locale),
                                    expiresDate: new Date(key.expiresAt).toLocaleDateString(locale),
                                  })
                                : t('createdDateNeverExpires', {
                                    date: new Date(key.createdAt).toLocaleDateString(locale),
                                  })}
                            </span>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <LoadingSpinner />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
